<template>
  <div class="space-y-3" data-test="authoring-assistant">
    <p class="text-gray-600">
      Say what to change in plain words. The AI edits the prompt and sections together and shows you the changes; nothing is saved until you accept and save the draft.
      It may ask a question first: answer it in the box. Each request takes up to a minute.
    </p>
    <ol v-if="conversation.length" class="space-y-3" data-test="authoring-turns">
      <li v-for="r in conversation" :key="r.id" class="space-y-1">
        <div class="ml-auto w-fit max-w-[85%] rounded-lg bg-neutral-100 px-3 py-2 text-gray-900">{{ r.instruction }}</div>
        <div :class="['w-fit max-w-[85%] rounded-lg border px-3 py-2', r.question && isDone(r) ? 'border-primary-300 bg-primary-50' : 'border-gray-200 bg-white']">
          <span v-if="!isDone(r)" class="text-gray-500">Working…</span>
          <span v-else-if="r.status === 'failed'" class="text-gray-900">{{ r.error_message || 'The AI could not do this.' }}</span>
          <span v-else-if="r.question" class="text-gray-900" data-test="authoring-question">{{ r.question }}</span>
          <span v-else class="text-gray-900">{{ r.summary }}<span v-if="r.outcome" class="ml-2 rounded bg-neutral-100 px-2 text-[11px] text-gray-700">{{ r.outcome }}</span></span>
          <div class="mt-1 text-[11px] text-gray-400">{{ r.model }}<span v-if="r.duration_ms"> · {{ (r.duration_ms / 1000).toFixed(0) }}s</span></div>
        </div>
      </li>
    </ol>
    <div v-if="error" role="alert" class="rounded-md bg-red-tint px-3 py-2 text-gray-900">{{ error }}</div>
    <textarea v-model="instruction" rows="3" :class="INPUT" :placeholder="awaitingAnswer ? 'Your answer…' : 'e.g. Drop the multimorbidity section and keep the provider summary to three sentences.'" :disabled="blocked" data-test="authoring-instruction" @keydown.meta.enter="ask('revise')" />
    <div class="flex items-center gap-3">
      <span v-if="dirty" class="text-gray-500">Save the draft first: the AI works on the saved draft.</span>
      <button v-if="conversation.length" type="button" :class="TEXT_BUTTON" :disabled="pending" data-test="authoring-new-conversation" @click="newConversation">New conversation</button>
      <button type="button" :class="[TEXT_BUTTON, 'ml-auto']" :disabled="blocked" data-test="derive-sections" @click="ask('derive_sections')">Derive sections from prompt</button>
      <button type="button" :class="PRIMARY_BUTTON" :disabled="blocked || !instruction.trim()" data-test="authoring-ask" @click="ask('revise')">{{ pending ? 'Working…' : (awaitingAnswer ? 'Reply' : 'Ask') }}</button>
    </div>

    <div v-if="proposal" class="fixed inset-0 z-20 flex items-center justify-center bg-gray-800/60" role="dialog" aria-modal="true" data-test="authoring-proposal">
      <div class="w-[900px] max-h-[85vh] flex flex-col rounded-lg bg-white border border-gray-200">
        <div class="bg-primary-100 px-4 py-3 rounded-t-lg font-semibold text-gray-900">Proposed changes</div>
        <div class="overflow-y-auto p-4 space-y-4">
          <p class="text-gray-900">{{ proposal.summary }}</p>
          <p v-if="proposal.draft_changed" class="rounded-md bg-neutral-100 px-3 py-2 text-gray-900" data-test="authoring-draft-changed">
            The draft was saved after this request. Accepting replaces the draft's prompt, schema and display with this proposal, which doesn't include those later changes.
          </p>
          <div v-for="d in proposalDiff" :key="d.key">
            <h3 class="text-[11px] font-medium uppercase tracking-wider text-gray-500 mb-2">{{ DIFF_LABELS[d.key] || d.key }}</h3>
            <pre class="whitespace-pre-wrap break-words"><span v-for="(l, k) in d.lines" :key="k" :class="['block px-2', l.op === '-' ? 'bg-gray-100 line-through text-gray-600' : (l.op === '+' ? 'bg-secondary-100 text-gray-900' : 'text-gray-700')]">{{ l.op }} {{ l.text }}</span></pre>
          </div>
          <p v-if="!proposalDiff.length" class="text-gray-600">The assistant proposed no changes.</p>
        </div>
        <div class="px-4 py-3 border-t border-gray-200 flex justify-end gap-3">
          <button type="button" :class="TEXT_BUTTON" data-test="authoring-reject" @click="decide('rejected')">Reject</button>
          <button type="button" :class="PRIMARY_BUTTON" :disabled="!proposalDiff.length" data-test="authoring-accept" @click="decide('accepted')">Accept into draft</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  createAuthoringRequest, getAuthoringRequest, listAuthoringRequests, recordAuthoringOutcome,
} from '@/services/api/hermesDrafts';
import { INPUT, PRIMARY_BUTTON, TEXT_BUTTON, diffParts, errorOf } from './hermes';

const POLL_MS = 1500;
const DIFF_KEYS = ['prompt', 'result_schema', 'display'];
const DIFF_LABELS = { prompt: 'Prompt', result_schema: 'Result schema', display: 'Display' };
const HISTORY_TURNS = 3;

export default {
  name: 'HermesAuthoringAssistant',
  props: {
    draftId: { type: Number, required: true },
    // Unsaved edits in the draft form. The assistant works on the saved draft, so it waits for a save.
    dirty: { type: Boolean, default: false },
  },
  emits: ['apply'],
  data() {
    return { requests: [], since: null, instruction: '', error: '', timer: null, proposal: null, INPUT, PRIMARY_BUTTON, TEXT_BUTTON, DIFF_LABELS };
  },
  computed: {
    turns() {
      return [...this.requests].reverse();
    },
    // The current conversation: requests since "New conversation" (all of them until then), oldest first.
    conversation() {
      return this.since ? this.turns.filter((r) => r.created_at >= this.since) : this.turns;
    },
    awaitingAnswer() {
      const last = this.conversation[this.conversation.length - 1];
      return !!last && this.isDone(last) && !!last.question;
    },
    pending() {
      return this.requests.some((r) => !this.isDone(r));
    },
    blocked() {
      return this.dirty || this.pending;
    },
    proposalDiff() {
      return this.proposal ? diffParts(this.proposal.base, this.proposal, DIFF_KEYS) : [];
    },
  },
  watch: {
    draftId: { immediate: true, handler: 'load' },
  },
  beforeUnmount() {
    clearTimeout(this.timer);
  },
  methods: {
    isDone(r) {
      return r.status === 'completed' || r.status === 'failed';
    },
    async load() {
      clearTimeout(this.timer);
      this.proposal = null;
      this.since = null;
      try {
        this.requests = await listAuthoringRequests(this.draftId);
      } catch (e) {
        this.requests = [];
        this.error = errorOf(e, 'Earlier requests could not be loaded.');
      }
      if (this.pending) this.poll();
    },
    history() {
      // The last few finished turns, oldest first, so a follow-up ("now shorten that one too") has context.
      return this.conversation.filter(this.isDone).slice(-HISTORY_TURNS).flatMap((r) => [
        { role: 'user', text: r.instruction.slice(0, 4000) },
        { role: 'assistant', text: (r.question || r.summary || r.error_message || '').slice(0, 4000) },
      ]).filter((t) => t.text);
    },
    newConversation() {
      this.since = new Date().toISOString();
      this.instruction = '';
    },
    async ask(mode) {
      if (this.blocked || (mode === 'revise' && !this.instruction.trim())) return;
      this.error = '';
      try {
        const created = await createAuthoringRequest(this.draftId, {
          mode, instruction: mode === 'revise' ? this.instruction.trim() : '', history: this.history(),
        });
        this.requests = [created, ...this.requests];
        if (mode === 'revise') this.instruction = '';
        this.poll();
      } catch (e) {
        this.error = errorOf(e, 'The request could not be sent.');
      }
    },
    async poll() {
      clearTimeout(this.timer);
      const open = this.requests.filter((r) => !this.isDone(r));
      try {
        const fresh = await Promise.all(open.map((r) => getAuthoringRequest(this.draftId, r.id)));
        this.requests = this.requests.map((r) => fresh.find((f) => f.id === r.id) || r);
        const proposed = fresh.find((r) => r.status === 'completed' && !r.question && r.prompt !== null);
        if (proposed) this.proposal = proposed;
      } catch (e) {
        this.error = errorOf(e, 'The request could not be loaded.');
        return;
      }
      if (this.pending) this.timer = setTimeout(() => this.poll(), POLL_MS);
    },
    async decide(outcome) {
      const proposal = this.proposal;
      this.proposal = null;
      if (outcome === 'accepted') {
        this.$emit('apply', { prompt: proposal.prompt, result_schema: proposal.result_schema, display: proposal.display });
      }
      try {
        const updated = await recordAuthoringOutcome(this.draftId, proposal.id, { outcome });
        this.requests = this.requests.map((r) => (r.id === updated.id ? updated : r));
      } catch {
        // Recording the outcome is bookkeeping; the change itself has already been applied or dropped.
      }
    },
  },
};
</script>
